import * as React from "react";

/**
 * The 3-2-1 before recording starts, centred over the scrim. The disc skips straight to recording;
 * the caption reassures. The digit swaps once a second with no tween, scale or bounce.
 */
export interface CountdownProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Seconds left. The caller ticks it once per second. */
  value: number;
  /** Caption pill below the disc. Default "Click anywhere to pause the countdown." Pass "" to hide. */
  hint?: string;
  /** Makes the disc a skip button that starts recording now. */
  onSkip?: () => void;
  /** Visible word under the digit when skippable. Default "Skip". */
  skipLabel?: string;
}

export function Countdown({
  value,
  hint = "Click anywhere to pause the countdown.",
  onSkip,
  skipLabel = "Skip",
  className,
  ...rest
}: CountdownProps): JSX.Element {
  const digit = (
    <span className="gk-countdown__digit gk-num" aria-hidden="true">
      {value}
    </span>
  );
  return (
    <div className={["gk-countdown", className].filter(Boolean).join(" ")} {...rest}>
      {onSkip ? (
        <button type="button" className="gk-countdown__disc" onClick={onSkip} aria-label="Skip the countdown and start recording">
          {digit}
          <span className="gk-countdown__skip" aria-hidden="true">
            {skipLabel}
          </span>
        </button>
      ) : (
        <div className="gk-countdown__disc">{digit}</div>
      )}
      {/* Live region outside the button: a button's children are presentational, so a live
          region inside it may never be announced. */}
      <span className="gk-countdown__live" aria-live="assertive" aria-atomic="true">
        {"Recording starts in " + value}
      </span>
      {hint && <span className="gk-countdown__hint">{hint}</span>}
    </div>
  );
}
